import React, { useContext, useState } from 'react';
import {
  FormPanel,
  FormTextField,
  FormCheckbox,
  FormFileField,
  FormSelect,
  useFormSelector,
  useStaticListSource,
  FormCustomField,
  FormContext,
  SnackbarContext,
  SnackbarVariant,
  FormDateField,
  useEventCallback,
} from '@eas/common-web';
import { HelpLabel } from '../../../components/help/help-label';
import { AddressField } from '../../../components/form/address-field/address-field';
import { StepOriginatorForm } from './step-originator-types';
import { PersonType } from '../../../models/uform';
import Button from '@material-ui/core/Button';
import SearchIcon from '@material-ui/icons/Search';
import { makeStyles } from '@material-ui/core/styles';
import { getSubjectByIco } from '../../subject/subjects-api';
import { useProcessContext } from '../../../components/process/process-context';

export const useStyles = makeStyles(() => ({
  actionButtonGroup: {
    width: 100,
  },
  actionButton: {
    width: 100,
    height: 22,
    padding: '0 10px',
    marginLeft: 3,
    fontSize: '12px',
    fontWeight: 600,
    flexShrink: 0,
  },
}));

export function StepOriginatorContent() {
  const {
    actorProxy,
    representedPersonType,
    differentContactAddress,
    representedPersonIco,
    executiveDeclaration,
  } = useFormSelector((values: StepOriginatorForm) => ({
    executiveDeclaration: values.actor?.executiveDeclaration,
    actorProxy: values.actor?.proxy,
    representedPersonType: values.representedPerson?.type,
    differentContactAddress: values?.representedPerson?.differentContactAddress,
    representedPersonIco: values.representedPerson?.ico,
  }));

  const classes = useStyles();

  const { steps } = useProcessContext();
  const isPrefilled = (fieldName: string) =>
    steps[1]?.disabledFields?.includes(fieldName);

  const { showSnackbar } = useContext(SnackbarContext);
  const { setFieldValue } = useContext<FormContext<StepOriginatorForm>>(
    FormContext
  );

  const representedPersonOptions = useStaticListSource([
    { id: PersonType.NATURAL_PERSON, name: 'Fyzická osoba' },
    { id: PersonType.BUSINESS_PERSON, name: 'Osoba s IČO' },
    { id: PersonType.FOREIGN_PERSON, name: 'Zahraniční osoba' },
  ]);

  const [isSeaerching, setIsSearching] = useState(false);
  const handleSearch = useEventCallback(async () => {
    setIsSearching(true);

    let subject = null;

    try {
      subject = await getSubjectByIco(representedPersonIco!).json();
    } catch (e) {
      showSnackbar('Subjekt se nepodařilo najít', SnackbarVariant.ERROR);
      setIsSearching(false);
      return;
    }

    showSnackbar('Subjekt nalezen', SnackbarVariant.SUCCESS);

    if (subject.name) {
      setFieldValue('representedPerson.companyName', subject.name);
    }

    if (subject.residence) {
      setFieldValue('representedPerson.contactAddress', subject.residence);
    }

    if (subject.isdsIds.length > 0) {
      setFieldValue('representedPerson.databox', subject.isdsIds[0].id);
    }

    setIsSearching(false);
  });

  return (
    <>
      <FormPanel label="Osoba činící úkon">
        <FormTextField
          name="actor.firstName"
          label={
            <HelpLabel label="Jméno" code="UFORM_FIRST_NAME" required={true} />
          }
          disabled={isPrefilled('actor.firstName')}
        />
        <FormTextField
          name="actor.lastName"
          label={
            <HelpLabel
              label="Příjmení"
              code="UFORM_LAST_NAME"
              required={true}
            />
          }
          disabled={isPrefilled('actor.lastName')}
        />
        <FormDateField
          name="actor.birthDate"
          label={
            <HelpLabel
              label="Datum narození"
              code="UFORM_BIRTH_DATE"
              required={true}
            />
          }
          disabled={isPrefilled('actor.birthDate')}
        />
        <FormTextField
          name="actor.idNumber"
          label={
            <HelpLabel
              label="Číslo OP"
              code="UFORM_BIRTH_ID_NUMBER"
              required={false}
            />
          }
          disabled={isPrefilled('actor.idNumber')}
        />
        <FormTextField
          name="actor.databox"
          label={
            <HelpLabel label="Datová schránka" code="UFORM_BIRTH_DATABOX" />
          }
          disabled={isPrefilled('actor.databox')}
        />
        <FormSelect
          name="actor.preferredResponseChannel"
          label={
            <HelpLabel
              label="Preferovaný kanál pro odpověď "
              code="UFORM_PREFERRED_RESPONSE_CHANNEL"
            />
          }
          valueIsId={true}
          // eslint-disable-next-line react-hooks/rules-of-hooks
          source={useStaticListSource([
            { id: 'ISDS', name: 'Datová schránka' },
            { id: 'MAIL', name: 'Pošta' },
          ])}
        />
        <FormTextField
          name="actor.email"
          label={<HelpLabel label="E-mail" code="UFORM_EMAIL" />}
        />
        <FormTextField
          name="actor.phone"
          label={<HelpLabel label="Telefonní číslo" code="UFORM_PHONE" />}
        />
        <AddressField
          name="actor.address"
          disableForeignCountry={true}
          disabled={isPrefilled('actor.address')}
        />
        <FormCheckbox
          name="actor.proxy"
          label={
            <HelpLabel
              label="Úkon prováděn v zastoupení"
              code="UFORM_ACTING_ON_BEhalf"
            />
          }
          notifyChange={() => {
            setFieldValue('actor.powerOfAttorney', null);
            setFieldValue('representedPerson', null);
          }}
        />
        {actorProxy && (
          <>
            <FormFileField
              name="actor.powerOfAttorney"
              label={
                <HelpLabel
                  label="Plná moc"
                  code="UFORM_POWER_OF_ATTORNEY"
                  required={!executiveDeclaration}
                />
              }
              accept={['.pdf']}
            />
            <FormSelect
              name="representedPerson.type"
              label={
                actorProxy ? (
                  <HelpLabel
                    label="Podání je činěno za"
                    code="UFORM_SUBMISSION_FOR"
                    required
                  />
                ) : (
                  <HelpLabel
                    label="Podání činí"
                    code="UFORM_SUBMISSION_FOR"
                    required
                  />
                )
              }
              valueIsId={true}
              source={representedPersonOptions}
              notifyChange={() => {
                setFieldValue('representedPerson.firstName', null);
                setFieldValue('representedPerson.lastName', null);
                setFieldValue('representedPerson.birthDate', null);
                setFieldValue('representedPerson.idNumber', null);
                setFieldValue('representedPerson.email', null);
                setFieldValue('representedPerson.phone', null);
                setFieldValue('representedPerson.ico', null);
                setFieldValue('representedPerson.companyName', null);
                setFieldValue('representedPerson.databox', null);
                setFieldValue('representedPerson.address', null);
                setFieldValue('representedPerson.contactAddress', null);
                setFieldValue(
                  'representedPerson.differentContactAddress',
                  null
                );
                setFieldValue('actor.executiveDeclaration', false);
              }}
            />
            {actorProxy &&
              (representedPersonType === PersonType.BUSINESS_PERSON ||
                representedPersonType === PersonType.FOREIGN_PERSON) && (
                <FormCheckbox
                  name="actor.executiveDeclaration"
                  label={
                    <HelpLabel
                      label="Čestně prohlašuji, že jsem jednatelem zastupované osoby"
                      code="UFORM_EXECUTIVE_DECLARATION"
                    />
                  }
                />
              )}
          </>
        )}
      </FormPanel>

      {representedPersonType === PersonType.NATURAL_PERSON && (
        <>
          <FormPanel label="Fyzická osoba">
            <FormTextField
              name="representedPerson.firstName"
              label={
                <HelpLabel
                  label="Jméno"
                  code="UFORM_FIRST_NAME"
                  required={true}
                />
              }
            />
            <FormTextField
              name="representedPerson.lastName"
              label={
                <HelpLabel
                  label="Příjmení"
                  code="UFORM_LAST_NAME"
                  required={true}
                />
              }
            />
            <FormDateField
              name="representedPerson.birthDate"
              label={
                <HelpLabel
                  label="Datum narození"
                  code="UFORM_BIRTH_DATE"
                  required={true}
                />
              }
            />
            <FormTextField
              name="representedPerson.idNumber"
              label={
                <HelpLabel
                  label="Číslo OP"
                  code="UFORM_INDIVIDUAL_ID_NUMBER"
                  required={true}
                />
              }
            />
            <FormTextField
              name="representedPerson.email"
              label={<HelpLabel label="E-mail" code="UFORM_EMAIL" />}
            />
            <FormTextField
              name="representedPerson.phone"
              label={<HelpLabel label="Telefonní číslo" code="UFORM_PHONE" />}
            />
            <FormTextField
              name="representedPerson.databox"
              label={
                <HelpLabel
                  label="Datová schránka zmocnitele"
                  code="UFORM_DATA_BOX"
                />
              }
            />
            <AddressField
              name="representedPerson.address"
              disableForeignCountry={true}
            />
            <FormCheckbox
              name="representedPerson.differentContactAddress"
              label={
                <HelpLabel
                  label="Kontaktní adresa je jiná než adresa trvalého pobytu"
                  code="UFORM_DIFFERENT_CONTACT_ADDRESS"
                />
              }
              notifyChange={() => {
                setFieldValue('representedPerson.contactAddress', null);
              }}
            />
          </FormPanel>
          {differentContactAddress && (
            <FormPanel label="Kontaktní adresa">
              <AddressField
                name="representedPerson.contactAddress"
                disableForeignCountry={true}
              />
            </FormPanel>
          )}
        </>
      )}

      {representedPersonType === PersonType.BUSINESS_PERSON && (
        <FormPanel label="Osoba s IČO">
          <FormCustomField
            name="representedPerson.icoWrapper"
            label={<HelpLabel label="IČO" code="UFORM_ICO" required={true} />}
          >
            <FormTextField
              name="representedPerson.ico"
              labelOptions={{
                hide: true,
              }}
              layoutOptions={{
                noSpacing: true,
                noUnderline: true,
              }}
            />
            <Button
              onClick={handleSearch}
              disabled={!representedPersonIco || isSeaerching}
              variant="text"
              startIcon={<SearchIcon />}
              className={classes.actionButton}
            >
              Hledat
            </Button>
          </FormCustomField>
          <FormTextField
            name="representedPerson.companyName"
            label={
              <HelpLabel
                label="Název"
                code="UFORM_COMPANY_NAME"
                required={true}
              />
            }
          />
          <FormTextField
            name="representedPerson.databox"
            label={<HelpLabel label="Datová schránka" code="UFORM_DATA_BOX" />}
          />
          <FormTextField
            name="representedPerson.email"
            label={<HelpLabel label="E-mail" code="UFORM_EMAIL" />}
          />
          <FormTextField
            name="representedPerson.phone"
            label={<HelpLabel label="Telefonní číslo" code="UFORM_PHONE" />}
          />
          <AddressField
            name="representedPerson.contactAddress"
            disableForeignCountry={true}
          />
        </FormPanel>
      )}

      {representedPersonType === PersonType.FOREIGN_PERSON && (
        <>
          <FormPanel label="Zahraniční osoba">
            <FormTextField
              name="representedPerson.ico"
              label={
                <HelpLabel
                  label="Zahraniční identifikátor (obdoba IČO)"
                  code="UFORM_FOREIGN_IDENTIFIER"
                  required={true}
                />
              }
            />
            <FormTextField
              name="representedPerson.companyName"
              label={
                <HelpLabel
                  label="Název"
                  code="UFORM_FOREIGN_COMPANY_NAME"
                  required={true}
                />
              }
            />
            <FormTextField
              name="representedPerson.email"
              label={<HelpLabel label="E-mail" code="UFORM_EMAIL" />}
            />
            <FormTextField
              name="representedPerson.phone"
              label={<HelpLabel label="Telefonní číslo" code="UFORM_PHONE" />}
            />
            <AddressField
              name="representedPerson.address"
              disableForeignCountry={false}
            />
          </FormPanel>

          <FormPanel label="Kontaktní adresa na území ČR">
            <AddressField
              name="representedPerson.contactAddress"
              disableForeignCountry={true}
            />
          </FormPanel>
        </>
      )}
    </>
  );
}
